/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, {
 useCallback,
 useEffect,
 useState,
} from 'react'
// import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import {
 activateUser,
 fetchUserList,
} from 'utils/api/auth/userManagementService'
import {IUser, IUserAction} from 'models/UserManagement'
import {ColumnProps, IFormField} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {DefaultApiResponse} from 'models/ApiResponse'
import {formatDateForDisplay} from 'utils/helper'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'

type IProps = {
 accessDetails: IFormField
 loggedInUserId: number | undefined
 actionDetails?: IUserAction | null
 setLoader: (newState: boolean) => void
 setActionDetails: (newState: IUserAction | null) => void
}

const ADMIN_CATEGORY_KEY = [
 'pmu_rp',
 'pmu_bayer',
 'offtaker_admin',
 'offtaker_user',
]

const Activate = (props: IProps) => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [currentPage, setCurrentPage] = useState(1)
 const [userListRes, setUserListRes] =
  useState<DefaultApiResponse>()
 const [userList, setUserList] = useState<IUser[]>([])
 const [itemsPerPage, setItemsPerPage] = useState(25)
 const [searchTerm, setSearchTerm] = useState('')
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>({})
 const [filterCount, setFilterCount] = useState(0)
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<IUser>>
 >([])
 const activateUserAccess =
  props.accessDetails?.activateUser?.section
 const userDetailsAccess =
  props.accessDetails?.userDetails?.section

 const getFilterConfig = useCallback(async () => {
  try {
   const res = await fetchFilterConfig({
    filter_type: 'USER',
   })
   if (res) {
    setFilterConfig(res?.applicableFilters ?? [])
   }
  } catch (error) {
   setFilterConfig([])
  }
 }, [])

 useEffect(() => {
  void getFilterConfig()
 }, [getFilterConfig])

 const getUserList = async (
  pageNo = 1,
  pageSize = 10,
  signal?: AbortSignal
 ) => {
  props.setLoader(true)
  try {
   let query: IFormField = {}
   let appliedFilters = 0
   if (filterConfig?.length) {
    const payload = await createFilterPayload(
     filterConfig,
     filterFormData
    )
    const {totalAppliedFilters, ...restPayload} = payload
    appliedFilters =
     typeof totalAppliedFilters === 'number'
      ? totalAppliedFilters
      : Number(totalAppliedFilters ?? 0)
    query = restPayload as IFormField
   }
   const trimmedSearch = searchTerm.trim()
   if (trimmedSearch) {
    query = {
     ...query,
     search: trimmedSearch,
    }
   }
   const res: DefaultApiResponse | null =
    await fetchUserList(
     'approved',
     pageNo,
     pageSize,
     Object.keys(query).length ? query : undefined,
     signal
    )
   if (res) {
    setUserListRes(res)
    setUserList(res.results ?? [])
   }
   setFilterCount(appliedFilters)
  } finally {
   props.setLoader(false)
  }
 }

 useEffect(() => {
  const abortController = new AbortController()
  if (currentPage !== 1) {
   setCurrentPage(1)
  }
  void getUserList(1, itemsPerPage, abortController.signal)
  return () => {
   abortController.abort()
  }
 }, [filterFormData, searchTerm, itemsPerPage])

 const handleItemsPerPageChange = (pageSize: number) => {
  setItemsPerPage(pageSize)
  setCurrentPage(1)
 }

 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getUserList(pageNo, itemsPerPage)
 }

 const handleActivateBtnClick = async (user: IUser) => {
  props.setActionDetails({
   userId: user.id.toString(),
   actionType: 'activate_user',
  })
 }

 const handleActionBtnClick = useCallback(
  (user: IUser) => {
   props.setActionDetails({
    userId: user.id.toString(),
    actionType: 'activate',
   })
  },
  [props.setActionDetails]
 )

 const activateAdminUser = async (user: IUser) => {
  try {
   const activeUserResponse = await activateUser(
    Number(user.id)
   )
   if (activeUserResponse) {
    toast.success(activeUserResponse?.message)
    await getUserList(currentPage, itemsPerPage)
   }
  } catch (error) {
   console.error('Error activating admin user:', error)
  }
 }

 const renderTableData = useCallback(async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IUser>> = [
   {
    key: 'srNo',
    title: t('userManagement.userListTable.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'full_name',
    title: t('userManagement.userListTable.name'),
    render: (_, record) => {
     return (
      <button
       type='button'
       disabled={!userDetailsAccess}
       className={`${
        userDetailsAccess && 'text-[#4E83E4] underline'
       } w-full text-left disabled:bg-transparent`}
       onClick={() => {
        if (userDetailsAccess)
         void handleActionBtnClick(record)
       }}
      >
       {record.full_name}
      </button>
     )
    },
   },
   {
    key: 'organisation',
    title: t('userManagement.userListTable.firmName'),
   },
   {
    key: 'phone_number',
    title: t('userManagement.userListTable.mobile'),
    className: 'w-[80px]',
   },
   {
    key: 'created_at',
    title: t(
     'userManagement.userListTable.userCreationDate'
    ),
    render: (_, record) => {
     return formatDateForDisplay(record?.created_at)
    },
   },
   {
    key: 'stakeholder',
    title: t('userManagement.userListTable.stakeholder'),
   },
   {
    key: 'category',
    title: t('userManagement.userListTable.category'),
   },
   {
    key: 'user_category',
    title: t('userManagement.userListTable.userCategory'),
   },
   {
    key: 'action',
    title: t('userManagement.userListTable.action'),
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       {activateUserAccess?.activate?.canView && (
        <button
         type='button'
         disabled={!activateUserAccess?.activate?.canEdit}
         className={`text-[#4E83E4] font-medium text-sm disabled:bg-transparent`}
         onClick={() => {
          if (activateUserAccess?.activate?.canEdit) {
           if (
            record.user_category_key &&
            ADMIN_CATEGORY_KEY.includes(
             record?.user_category_key
            )
           ) {
            void activateAdminUser(record)
            return
           }
           void handleActivateBtnClick(record)
          }
         }}
        >
         {t('userManagement.activate.activate')}
        </button>
       )}
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }, [
  handleActionBtnClick,
  props.setLoader,
  t,
  activateUserAccess,
  userDetailsAccess,
 ])

 useEffect(() => {
  void getUserList(currentPage, itemsPerPage)
 }, [props.actionDetails])

 useEffect(() => {
  if (userList) {
   void renderTableData()
  }
 }, [renderTableData, userList])

 return (
  <>
   <div className='w-full'>
    {/* <div className='flex items-center justify-between text-[#171B1F] bg-[#F5F8FA] text-xl font-bold rounded-lg p-4 mb-6'>
     <div className='flex items-center '>
      <Button
       disabled={false}
       onClick={() => {}}
       color='white'
       className='text-center py-[9px] p-3 bg-white 
                          !rounded-lg text-sm !text-[#4E83E4] font-semibold border-[#E6EBF0]'
      >
       <KTSVG
        path={`/media/icons/filter.svg`}
        svgClassName={`w-4 h-4 text-white mr-2`}
       />
       {t('helperText.filter')}
      </Button>
     </div>
    </div> */}
    <div>
     <div className='my-6'>
      <div className='grid md:grid-cols-2 items-center gap-4'>
       <KTSearchBox
        label={t('userManagement.activate.search')}
        placeholder={t(
         'userManagement.activate.searchPlaceholder'
        )}
        setSearchTerm={setSearchTerm}
       />
       {filterConfig?.length > 0 && (
        <div className='flex gap-4 items-center justify-end'>
         <KTFilterBox
          parentId='activate'
          btnText={t('userManagement.activate.filters')}
          containerClass='bg-white border-none mt-0 pl-0 justify-end'
          filterConfig={filterConfig}
          filterFormData={filterFormData ?? {}}
          setFilterFormData={setFilterFormData}
          filterCount={filterCount}
          setLoader={props.setLoader}
          showDateRangeBox={false}
         />
        </div>
       )}
      </div>
     </div>
     <KTTable
      data={userList}
      columns={tableColList}
      headerClass='bg-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
      tableClass=''
      rowClass='even:bg-[#E6EBF0]'
      showPagination={true}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={userListRes?.count}
      onPageChange={handlePageChange}
      setItemsPerPage={handleItemsPerPageChange}
     />
    </div>
   </div>
  </>
 )
}

export default Activate
